import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Button,
  InputAdornment,
  CardHeader,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  CalendarToday as CalendarIcon,
  AccountCircle as AccountCircleIcon,
  MenuBook as CurricularIcon,
  EmojiEvents as ExtracurricularIcon,
  SwapHoriz as TransferIcon,
} from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { TABLES, RELATIONS, getModules, getCreditTypes, getPredefinedActivities, getCurrentSchoolYear, getSchoolYears, resolveSchoolYear } from '../lib/tables';
import SchoolYearSelect from '../components/SchoolYearSelect';
import { useAuth } from '../context/AuthContext';

export default function TeacherStudents() {
  const { profile } = useAuth();
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState('all');
  const [classes, setClasses] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetailsOpen, setStudentDetailsOpen] = useState(false);
  const [studentRequests, setStudentRequests] = useState([]);
  const [studentCredits, setStudentCredits] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  const [statusFilter, setStatusFilter] = useState('toate');
  const [tipFilter, setTipFilter] = useState('toate');
  const [moduleFilter, setModuleFilter] = useState('toate');
  const [sortBy, setSortBy] = useState('data_desc');
  const [selectedYear, setSelectedYear] = useState(null);
  const [yearOptions, setYearOptions] = useState([]);

  const getSpecializationName = (code) => {
    const specializationsMap = {
      'MI': 'Matematică-Informatică',
      'SN': 'Științe ale Naturii',
      'SS': 'Științe Sociale',
      'FL': 'Filologie',
      'BU': 'Business',
    };
    return specializationsMap[code] || code || 'N/A';
  };

  const getTeacherCreditTypes = async () => {
    try {
      const { data } = await supabase
        .from(TABLES.credit_types)
        .select('id')
        .in('validator_rol', ['profesor', 'coordonator']);
      
      return data?.map(item => item.id) || [1, 3];
    } catch {
      return [1, 3];
    }
  };

  useEffect(() => {
    let mounted = true;
    getSchoolYears(TABLES.credit_requests).then((years) => {
      if (!mounted) return;
      setYearOptions(years);
      setSelectedYear((prev) => prev ?? resolveSchoolYear(years));
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const loadAsync = async () => {
      if (profile && profile.rol === 'profesor' && isMounted && selectedYear) {
        await loadStudents(isMounted);
      }
    };
    
    loadAsync();
    return () => { isMounted = false; };
  }, [profile, selectedYear]);

  const loadStudents = async (isMounted = true) => {
    setLoading(true);
    try {
      // tipurile de credit pentru mapping categorii
      const { data: creditTypes } = await supabase
        .from(TABLES.credit_types)
        .select('id, nume_tip, categorie');
      
      if (!isMounted) return;

      const creditTypesMap = {};
      creditTypes?.forEach(ct => {
        creditTypesMap[ct.id] = ct.categorie || ct.nume_tip?.toLowerCase() || '';
      });

      const { data: studentsData } = await supabase
        .from(TABLES.profiles)
        .select('*')
        .eq('rol', 'elev')
        .order('clasa')
        .order('nume');

      if (!isMounted) return;
      
      if (!studentsData || studentsData.length === 0) {
        if (isMounted) {
          setStudents([]);
          setFilteredStudents([]);
          setLoading(false);
        }
        return;
      }

      const teacherCreditTypes = await getTeacherCreditTypes();
      const yearFilter = selectedYear === 'all' ? yearOptions : [selectedYear || getCurrentSchoolYear()];
      if (!isMounted) return;
      
      const studentsWithDetails = await Promise.all(
        studentsData.map(async (student) => {
          try {
            const { data: requestsData } = await supabase
              .from(TABLES.credit_requests)
              .select('id, status, id_tip_credit, credite_solicitate, credite_acordate')
              .eq('id_elev', student.id)
              .in('an_scolar', yearFilter)
              .in('id_tip_credit', teacherCreditTypes);

            // cereri validate pentru breakdown pe categorii
            const { data: allValidatedRequests } = await supabase
              .from(TABLES.credit_requests)
              .select('id_tip_credit, credite_solicitate, credite_acordate')
              .eq('id_elev', student.id)
              .in('an_scolar', yearFilter)
              .eq('status', 'validat');

            const pendingRequests = requestsData?.filter(r => r.status === 'in_asteptare').length || 0;
            
            // credite pe categorii
            let crediteCurriculare = 0;
            let crediteExtracurriculare = 0;
            let crediteTransferabile = 0;

            (allValidatedRequests || []).forEach(req => {
              const categorie = creditTypesMap[req.id_tip_credit] || '';
              const credite = req.credite_acordate || req.credite_solicitate || 0;
              if (categorie === 'curricular' || (categorie.includes && categorie.includes('curricular') && !categorie.includes('extra'))) {
                crediteCurriculare += credite;
              } else if (categorie === 'extracurricular' || (categorie.includes && categorie.includes('extracurricular'))) {
                crediteExtracurriculare += credite;
              } else if (categorie === 'transferabil' || (categorie.includes && categorie.includes('transfer'))) {
                crediteTransferabile += credite;
              }
            });

            // total credite din cererile selectate
            const totalCredits = crediteCurriculare + crediteExtracurriculare + crediteTransferabile;
            
            // modulele active nu se mai calculeaza din credits_obtained
            const activeModules = 0;

            return {
              ...student,
              totalCredits,
              crediteCurriculare,
              crediteExtracurriculare,
              crediteTransferabile,
              pendingRequests,
              activeModules,
              modulesProgress: [],
            };
          } catch (err) {
            console.error('Error loading student:', err);
            return {
              ...student,
              totalCredits: 0,
              crediteCurriculare: 0,
              crediteExtracurriculare: 0,
              crediteTransferabile: 0,
              pendingRequests: 0,
              activeModules: 0,
              modulesProgress: [],
            };
          }
        })
      );

      if (!isMounted) return;

      setStudents(studentsWithDetails);
      setFilteredStudents(studentsWithDetails);

      const uniqueClasses = [...new Set(studentsWithDetails
        .map(s => s.clasa)
        .filter(Boolean)
        .sort())];
      
      setClasses(uniqueClasses);

    } catch (error) {
      console.error('Error loading students:', error);
      if (isMounted) {
        setStudents([]);
        setFilteredStudents([]);
      }
    } finally {
      if (isMounted) setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    let filtered = students;

    if (selectedClass !== 'all') {
      filtered = filtered.filter(s => s.clasa === selectedClass);
    }

    if (term) {
      filtered = filtered.filter(s =>
        (s.nume?.toLowerCase().includes(term) || false) ||
        (s.prenume?.toLowerCase().includes(term) || false) ||
        (s.email?.toLowerCase().includes(term) || false) ||
        (s.clasa?.toLowerCase().includes(term) || false) ||
        ((s.specializare || s.specialitate)?.toLowerCase().includes(term) || false)
      );
    }

    setFilteredStudents(filtered);
  };

  const handleViewStudentDetails = async (student) => {
    setSelectedStudent(student);
    setDetailsLoading(true);
    
    setStatusFilter('toate');
    setTipFilter('toate');
    setModuleFilter('toate');
    setSortBy('data_desc');
    
    try {
      const teacherCreditTypes = await getTeacherCreditTypes();
      const yearFilter = selectedYear === 'all' ? yearOptions : [selectedYear || getCurrentSchoolYear()];
      
      const { data: requests } = await supabase
        .from(TABLES.credit_requests)
        .select(`
          *,
          ${RELATIONS.credit_types}!inner(nume_tip, validator_rol, culoare),
          ${RELATIONS.modules}!inner(cod_module, nume_module),
          ${RELATIONS.predefined_activities}!left(credite_per_ora, descriere)
        `)
        .eq('id_elev', student.id)
        .in('an_scolar', yearFilter)
        .in('id_tip_credit', teacherCreditTypes)
        .order('data_cerere', { ascending: false });

      const { data: credits } = await supabase
        .from(TABLES.credits_obtained)
        .select(`
          credite_acumulate,
          ${RELATIONS.modules}!inner(cod_module, nume_module)
        `)
        .eq('id_elev', student.id);

      setStudentRequests(requests || []);
      setStudentCredits(credits || []);
      setStudentDetailsOpen(true);

    } catch {
      // eroare ignorata
    } finally {
      setDetailsLoading(false);
    }
  };

  const calculateStudentStats = () => {
    if (!selectedStudent) return { totalCredits: 0, approved: 0, rejected: 0, pending: 0 };
    
    const approved = studentRequests.filter(r => r.status === 'validat').length;
    const rejected = studentRequests.filter(r => r.status === 'respins').length;
    const pending = studentRequests.filter(r => r.status === 'in_asteptare').length;
    const totalCredits = studentCredits.reduce((sum, c) => sum + (c.credite_acumulate || 0), 0);
    
    return { totalCredits, approved, rejected, pending };
  };

  const calculateEstimatedCredits = (request) => {
    if (!request.ore_realizate) return 0;
    
    const creditsPerHour = getPredefinedActivities(request)?.credite_per_ora || 
                         (getCreditTypes(request)?.nume_tip?.toLowerCase() === 'curricular' ? 0.10 : 1.00);
    const estimated = request.ore_realizate * creditsPerHour;
    
    return Math.max(Math.ceil(estimated), 1);
  };

  const getFilteredAndSortedRequests = () => {
    let filtered = [...studentRequests];

    if (statusFilter !== 'toate') {
      filtered = filtered.filter(request => request.status === statusFilter);
    }

    if (tipFilter !== 'toate') {
      filtered = filtered.filter(request => getCreditTypes(request)?.nume_tip === tipFilter);
    }

    if (moduleFilter !== 'toate') {
      filtered = filtered.filter(request => getModules(request)?.cod_module === moduleFilter);
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'data_desc':
          return new Date(b.data_cerere) - new Date(a.data_cerere);
        case 'data_asc':
          return new Date(a.data_cerere) - new Date(b.data_cerere);
        case 'credite_desc':
          const crediteA = a.status === 'validat' ? (a.credite_acordate || 0) : calculateEstimatedCredits(a);
          const crediteB = b.status === 'validat' ? (b.credite_acordate || 0) : calculateEstimatedCredits(b);
          return crediteB - crediteA;
        case 'ore_desc':
          return (b.ore_realizate || 0) - (a.ore_realizate || 0);
        default:
          return 0;
      }
    });

    return filtered;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const studentStats = calculateStudentStats();
  const filteredRequests = getFilteredAndSortedRequests();
  
  const uniqueStatuses = [...new Set(studentRequests.map(r => r.status))];
  const uniqueTypes = [...new Set(studentRequests.map(r => getCreditTypes(r)?.nume_tip).filter(Boolean))];
  const uniqueModules = [...new Set(studentRequests.map(r => getModules(r)?.cod_module).filter(Boolean))];

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      {/* Header îmbunătățit */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
              <SchoolIcon fontSize="large" />
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Elevii Mei
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Gestionează și urmărește progresul elevilor tăi
              </Typography>
            </Box>
          </Box>
          {selectedYear && (
            <SchoolYearSelect value={selectedYear} options={yearOptions} onChange={setSelectedYear} />
          )}
        </Box>
        
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Vezi doar elevii pentru care ești validator (activități curricular și transferabil)
        </Alert>
      </Box>

      {/* Statistici cards - UI îmbunătățit */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, width: '100%', mb: 4 }}>
        <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
          <Card sx={{ 
            height: '100%',
            width: '100%',
            minHeight: 120,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            p: { xs: 2, sm: 3 },
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 0 }}>
              <Typography variant="h3" fontWeight="bold" color="primary" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                {students.length}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                Total Elevi
              </Typography>
            </CardContent>
          </Card>
        </Box>
        
        <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
          <Card sx={{ 
            height: '100%',
            width: '100%',
            minHeight: 120,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            p: { xs: 2, sm: 3 },
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 0 }}>
              <Typography variant="h3" fontWeight="bold" color="success.main" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                {students.reduce((sum, s) => sum + (s.totalCredits || 0), 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                Credite Totale
              </Typography>
            </CardContent>
          </Card>
        </Box>
        
        <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
          <Card sx={{ 
            height: '100%',
            width: '100%',
            minHeight: 120,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            p: { xs: 2, sm: 3 },
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 0 }}>
              <Typography variant="h3" fontWeight="bold" color="warning.main" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                {students.reduce((sum, s) => sum + (s.pendingRequests || 0), 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                Cereri În Așteptare
              </Typography>
            </CardContent>
          </Card>
        </Box>
        
        <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
          <Card sx={{ 
            height: '100%',
            width: '100%',
            minHeight: 120,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            p: { xs: 2, sm: 3 },
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 0 }}>
              <Typography variant="h3" fontWeight="bold" color="info.main" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                {students.reduce((sum, s) => sum + (s.activeModules || 0), 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                Module Active
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Card de filtrare - UI îmbunătățit */}
      <Card sx={{ mb: 4, borderRadius: 2 }}>
        <CardHeader 
          title="Filtrează elevii"
          avatar={<FilterListIcon color="primary" />}
          sx={{ pb: 0 }}
        />
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Caută elev după nume, prenume, clasă sau specializare..."
                value={searchTerm}
                onChange={handleSearch}
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                size="medium"
                label="Clasă"
                value={selectedClass}
                onChange={(e) => {
                  setSelectedClass(e.target.value);
                  handleSearch({ target: { value: searchTerm } });
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
                SelectProps={{ native: true }}
              >
                <option value="all">Toate clasele</option>
                {classes.map((cls) => (
                  <option key={cls} value={cls}>
                    {cls}
                  </option>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={`${filteredStudents.length} elevi`}
                  color="primary"
                  sx={{ 
                    flex: 1,
                    borderRadius: 2,
                    height: 40,
                    '& .MuiChip-label': { fontWeight: 'medium' }
                  }}
                />
                <Tooltip title="Reîncarcă lista">
                  <IconButton onClick={loadStudents} size="small">
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabel elevii - UI îmbunătățit */}
      <Card sx={{ borderRadius: 2 }}>
        <CardHeader 
          title="Lista elevilor"
          subheader={`${filteredStudents.length} elevi găsiți`}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        />
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Elev</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Clasă</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>
                    <Tooltip title="Credite Curriculare">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <CurricularIcon fontSize="small" color="primary" />
                        Curr.
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>
                    <Tooltip title="Credite Extracurriculare">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <ExtracurricularIcon fontSize="small" color="warning" />
                        Extra.
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>
                    <Tooltip title="Credite Transferabile">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <TransferIcon fontSize="small" color="success" />
                        Trans.
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Cereri</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Acțiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStudents.map((student) => (
                  <TableRow 
                    key={student.id} 
                    hover
                    sx={{ 
                      '&:hover': { bgcolor: '#f5f9ff' },
                      '&:last-child td': { borderBottom: 0 }
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: 'primary.main',
                            width: 40,
                            height: 40,
                            border: '2px solid',
                            borderColor: 'primary.light'
                          }}
                        >
                          {student.prenume?.[0]}{student.nume?.[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {student.nume} {student.prenume}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <EmailIcon fontSize="inherit" />
                            {student.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    
                    <TableCell sx={{ py: 2 }}>
                      <Chip 
                        label={student.clasa || 'N/A'} 
                        size="small" 
                        sx={{ 
                          bgcolor: '#e3f2fd',
                          color: 'primary.main',
                          fontWeight: 'medium'
                        }}
                      />
                    </TableCell>
                    
                    <TableCell align="center" sx={{ py: 2 }}>
                      <Chip 
                        icon={<CurricularIcon />}
                        label={student.crediteCurriculare || 0}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    
                    <TableCell align="center" sx={{ py: 2 }}>
                      <Chip 
                        icon={<ExtracurricularIcon />}
                        label={student.crediteExtracurriculare || 0}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    </TableCell>
                    
                    <TableCell align="center" sx={{ py: 2 }}>
                      <Chip 
                        icon={<TransferIcon />}
                        label={student.crediteTransferabile || 0}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    </TableCell>
                    
                    <TableCell align="center" sx={{ py: 2 }}>
                      <Typography variant="body1" fontWeight="bold" color="primary">
                        {student.totalCredits || 0}
                      </Typography>
                    </TableCell>
                    
                    <TableCell sx={{ py: 2 }}>
                      {student.pendingRequests > 0 ? (
                        <Chip 
                          label={student.pendingRequests} 
                          color="warning" 
                          size="small"
                          icon={<PendingIcon fontSize="small" />}
                          sx={{ fontWeight: 'bold' }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    
                    <TableCell sx={{ py: 2 }}>
                      <Tooltip title="Vezi detalii elev">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PersonIcon />}
                          onClick={() => handleViewStudentDetails(student)}
                          sx={{ borderRadius: 2 }}
                        >
                          Detalii
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredStudents.length === 0 && (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <SchoolIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nu s-au găsit elevi
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Încearcă să schimbi filtrele de căutare sau verifică dacă ai elevi atribuiți
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Dialog detalii elev - CU FILTRE FUNCȚIONALE */}
      {selectedStudent && (
        <Dialog 
          open={studentDetailsOpen} 
          onClose={() => setStudentDetailsOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ 
                bgcolor: 'primary.main', 
                width: 56, 
                height: 56,
                border: '3px solid',
                borderColor: 'primary.light'
              }}>
                {selectedStudent.prenume?.[0]}{selectedStudent.nume?.[0]}
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  {selectedStudent.nume} {selectedStudent.prenume}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SchoolIcon fontSize="small" />
                  {selectedStudent.clasa} • {getSpecializationName(selectedStudent.specializare || selectedStudent.specialitate)}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          
          <DialogContent sx={{ pt: 3 }}>
            {detailsLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress size={60} />
              </Box>
            ) : (
              <>
                <Tabs 
                  value={activeTab} 
                  onChange={(e, v) => setActiveTab(v)} 
                  sx={{ 
                    mb: 3,
                    borderBottom: 1,
                    borderColor: 'divider'
                  }}
                >
                  <Tab 
                    label="Statistici" 
                    icon={<AccountCircleIcon />} 
                    iconPosition="start"
                    sx={{ minHeight: 48 }}
                  />
                  <Tab 
                    label={`Istoric Cereri (${studentRequests.length})`} 
                    icon={<CalendarIcon />} 
                    iconPosition="start"
                    sx={{ minHeight: 48 }}
                  />
                  <Tab 
                    label="Sumar Credite" 
                    icon={<SchoolIcon />} 
                    iconPosition="start"
                    sx={{ minHeight: 48 }}
                  />
                </Tabs>
                
                {activeTab === 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, width: '100%', mb: 3 }}>
                      <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
                        <Card sx={{ height: '100%', width: '100%', minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', p: { xs: 2, sm: 3 } }}>
                          <CardContent sx={{ textAlign: 'center', p: 0 }}>
                            <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                              {studentStats.totalCredits}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                              Credite Totale
                            </Typography>
                          </CardContent>
                        </Card>
                      </Box>
                      <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
                        <Card sx={{ height: '100%', width: '100%', minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', p: { xs: 2, sm: 3 }, bgcolor: '#e8f5e9' }}>
                          <CardContent sx={{ textAlign: 'center', p: 0 }}>
                            <Typography variant="h3" color="success.main" fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                              {studentStats.approved}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                              Cereri Acceptate
                            </Typography>
                          </CardContent>
                        </Card>
                      </Box>
                      <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
                        <Card sx={{ height: '100%', width: '100%', minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', p: { xs: 2, sm: 3 }, bgcolor: '#ffebee' }}>
                          <CardContent sx={{ textAlign: 'center', p: 0 }}>
                            <Typography variant="h3" color="error.main" fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                              {studentStats.rejected}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                              Cereri Respinse
                            </Typography>
                          </CardContent>
                        </Card>
                      </Box>
                      <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
                        <Card sx={{ height: '100%', width: '100%', minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', p: { xs: 2, sm: 3 }, bgcolor: '#fff3e0' }}>
                          <CardContent sx={{ textAlign: 'center', p: 0 }}>
                            <Typography variant="h3" color="warning.main" fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                              {studentStats.pending}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                              În Așteptare
                            </Typography>
                          </CardContent>
                        </Card>
                      </Box>
                    </Box>

                    <Card sx={{ mt: 3 }}>
                      <CardHeader title="Informații contact" />
                      <CardContent>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                              <EmailIcon color="primary" />
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Email
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  {selectedStudent.email}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                              <PhoneIcon color="primary" />
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Telefon
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  {selectedStudent.telefon || 'Nu este setat'}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Box>
                )}
                
                {activeTab === 1 && (
                  <Box>
                    {/* Card filtre */}
                    <Card sx={{ mb: 3, borderRadius: 2 }}>
                      <CardHeader 
                        title="Filtrează cererile"
                        avatar={<FilterListIcon color="primary" />}
                        action={
                          <TextField
                            select
                            size="small"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            sx={{ minWidth: 200 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SortIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                            SelectProps={{ native: true }}
                          >
                            <option value="data_desc">Data (cele mai noi)</option>
                            <option value="data_asc">Data (cele mai vechi)</option>
                            <option value="credite_desc">Credite (descrescător)</option>
                            <option value="ore_desc">Ore (descrescător)</option>
                          </TextField>
                        }
                      />
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="Status"
                              value={statusFilter}
                              onChange={(e) => setStatusFilter(e.target.value)}
                            >
                              <option value="toate">Toate statusurile</option>
                              <option value="validat">Aprobate</option>
                              <option value="respins">Respinse</option>
                              <option value="in_asteptare">În așteptare</option>
                            </TextField>
                          </Grid>
                          
                          {uniqueTypes.length > 0 && (
                            <Grid item xs={12} sm={4}>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label="Tip credit"
                                value={tipFilter}
                                onChange={(e) => setTipFilter(e.target.value)}
                              >
                                <option value="toate">Toate tipurile</option>
                                {uniqueTypes.map(type => (
                                  <option key={type} value={type}>
                                    {type}
                                  </option>
                                ))}
                              </TextField>
                            </Grid>
                          )}
                          
                          {uniqueModules.length > 0 && (
                            <Grid item xs={12} sm={4}>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label="Modul"
                                value={moduleFilter}
                                onChange={(e) => setModuleFilter(e.target.value)}
                              >
                                <option value="toate">Toate modulele</option>
                                {uniqueModules.map(module => (
                                  <option key={module} value={module}>
                                    {module}
                                  </option>
                                ))}
                              </TextField>
                            </Grid>
                          )}
                        </Grid>
                        
                        {/* Indicatori filtre active */}
                        {(statusFilter !== 'toate' || tipFilter !== 'toate' || moduleFilter !== 'toate') && (
                          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {statusFilter !== 'toate' && (
                              <Chip 
                                label={`Status: ${statusFilter === 'validat' ? 'Aprobate' : statusFilter === 'respins' ? 'Respinse' : 'În așteptare'}`}
                                size="small"
                                onDelete={() => setStatusFilter('toate')}
                                color="primary"
                                variant="outlined"
                              />
                            )}
                            {tipFilter !== 'toate' && (
                              <Chip 
                                label={`Tip: ${tipFilter}`}
                                size="small"
                                onDelete={() => setTipFilter('toate')}
                                color="primary"
                                variant="outlined"
                              />
                            )}
                            {moduleFilter !== 'toate' && (
                              <Chip 
                                label={`Modul: ${moduleFilter}`}
                                size="small"
                                onDelete={() => setModuleFilter('toate')}
                                color="primary"
                                variant="outlined"
                              />
                            )}
                            <Button 
                              size="small" 
                              onClick={() => {
                                setStatusFilter('toate');
                                setTipFilter('toate');
                                setModuleFilter('toate');
                              }}
                            >
                              Șterge toate filtrele
                            </Button>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                    
                    {/* Tabel cereri */}
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, borderRadius: 2 }}>
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell><strong>Activitate</strong></TableCell>
                            <TableCell><strong>Tip</strong></TableCell>
                            <TableCell><strong>Modul</strong></TableCell>
                            <TableCell><strong>Credite</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Data</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredRequests.map((request) => (
                            <TableRow 
                              key={request.id} 
                              hover
                              sx={{ 
                                '&:hover': { bgcolor: '#f5f9ff' },
                                opacity: request.status === 'respins' ? 0.8 : 1
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {request.nume_activitate}
                                </Typography>
                                {request.descriere && (
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    {request.descriere.substring(0, 80)}...
                                  </Typography>
                                )}
                                {request.ore_realizate && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {request.ore_realizate} ore
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={getCreditTypes(request)?.nume_tip || 'N/A'}
                                  size="small"
                                  sx={{
                                    backgroundColor: `${getCreditTypes(request)?.culoare || '#1976d2'}20`,
                                    color: getCreditTypes(request)?.culoare || '#1976d2',
                                    fontWeight: 'bold',
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {getModules(request)?.cod_module || 'N/A'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {getModules(request)?.nume_module || ''}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                {request.status === 'validat' ? (
                                  <Box>
                                    <Typography variant="h6" fontWeight="bold" color="success.main">
                                      {request.credite_acordate || 0}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {request.ore_realizate} ore × {getPredefinedActivities(request)?.credite_per_ora?.toFixed(2) || '1.00'}
                                    </Typography>
                                  </Box>
                                ) : request.status === 'in_asteptare' ? (
                                  <Box>
                                    <Typography variant="h6" color="warning.main" fontWeight="bold">
                                      {calculateEstimatedCredits(request)}*
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      estimate
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  icon={
                                    request.status === 'validat' ? <CheckCircleIcon fontSize="small" /> :
                                    request.status === 'respins' ? <CancelIcon fontSize="small" /> : 
                                    <PendingIcon fontSize="small" />
                                  }
                                  label={
                                    request.status === 'validat' ? 'Aprobată' :
                                    request.status === 'respins' ? 'Respinsă' : 'În așteptare'
                                  }
                                  size="small"
                                  color={
                                    request.status === 'validat' ? 'success' :
                                    request.status === 'respins' ? 'error' : 'warning'
                                  }
                                  sx={{ fontWeight: 'bold', minWidth: 100 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {new Date(request.data_cerere).toLocaleDateString('ro-RO')}
                                  </Typography>
                                  {request.data_validare && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Validat: {new Date(request.data_validare).toLocaleDateString('ro-RO')}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {filteredRequests.length === 0 && (
                      <Box sx={{ py: 6, textAlign: 'center' }}>
                        <PendingIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          Nu s-au găsit cereri
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {studentRequests.length === 0 
                            ? 'Acest elev nu are nicio cerere în sistem'
                            : 'Încearcă să schimbi filtrele de căutare'
                          }
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
                
                {activeTab === 2 && (
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                      Sumar Credite
                    </Typography>
                    
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} sm={4}>
                        <Card variant="outlined" sx={{ bgcolor: '#E3F2FD', borderColor: '#2196F3', textAlign: 'center', py: 2 }}>
                          <CardContent>
                            <Typography variant="h4" fontWeight="bold" color="#1976D2">
                              {selectedStudent?.crediteCurriculare || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Curriculare
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Card variant="outlined" sx={{ bgcolor: '#F3E5F5', borderColor: '#9C27B0', textAlign: 'center', py: 2 }}>
                          <CardContent>
                            <Typography variant="h4" fontWeight="bold" color="#7B1FA2">
                              {selectedStudent?.crediteExtracurriculare || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Extracurriculare
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Card variant="outlined" sx={{ bgcolor: '#FFF3E0', borderColor: '#FF9800', textAlign: 'center', py: 2 }}>
                          <CardContent>
                            <Typography variant="h4" fontWeight="bold" color="#F57C00">
                              {selectedStudent?.crediteTransferabile || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Transferabile
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                    
                    <Card sx={{ bgcolor: 'primary.main', color: 'white', textAlign: 'center', py: 2 }}>
                      <CardContent>
                        <Typography variant="h3" fontWeight="bold">
                          {selectedStudent?.totalCredits || 0}
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                          TOTAL CREDITE
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                )}
              </>
            )}
          </DialogContent>
          
          <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
            <Button 
              onClick={() => setStudentDetailsOpen(false)}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              Închide
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
}

const RefreshIcon = ({ fontSize = 'small' }) => (
  <svg width={fontSize === 'small' ? 20 : 24} height={fontSize === 'small' ? 20 : 24} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
  </svg>
);